import { visibleFallback } from "@/lib/cms/cms-new-i18n";
import type { ImageLinkReport } from "@/api/image-link";

const FALLBACKS: Record<string, string> = {
  "bo.imageLink.ladder.exact": "correspondance exacte",
  "bo.imageLink.ladder.caseIgnored": "casse ignorée",
  "bo.imageLink.ladder.spacesIgnored": "espaces et tirets ignorés",
  "bo.imageLink.ladder.accentsIgnored": "accents ignorés",
  "bo.imageLink.ladder.leadingZerosIgnored": "zéros initiaux ignorés",
  "bo.imageLink.ladder.suffixIgnored": "suffixe ignoré",
  "bo.imageLink.ladder.numberInName": "numéro trouvé dans le nom",
  "bo.imageLink.ladder.suffix": "suffixe « {{suffix}} »",
  "bo.imageLink.headline.empty": "Aucune image sélectionnée.",
  "bo.imageLink.headline.main":
    "{{auto}} de vos {{total}} image(s) correspond(ent) sans ambiguïté{{recordsPart}}",
  "bo.imageLink.headline.recordsPart":
    " à {{records}} fiche(s)",
  "bo.imageLink.headline.needsDecision":
    "{{count}} demande(nt) votre validation",
  "bo.imageLink.headline.unlinkable":
    "{{count}} ne correspond(ent) à aucun numéro d'enregistrement",
};

type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

function interpolate(
  template: string,
  params: Record<string, string | number> = {},
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    params[key] != null ? String(params[key]) : "",
  );
}

function tKey(
  t: TranslateFn,
  key: string,
  params: Record<string, string | number> = {},
): string {
  const fallback = FALLBACKS[key] ?? key;
  const translated = t(key, {
    ...params,
    defaultValue: visibleFallback(key, interpolate(fallback, params)),
  });
  return translated === key ? interpolate(fallback, params) : translated;
}

/** Libellé d'une règle de ladder (clé + params). */
export function translateImageLinkRule(
  t: TranslateFn,
  rule: { key: string; params?: Record<string, string | number> } | string,
): string {
  // Anciens payloads : chaîne déjà localisée.
  if (typeof rule === "string") return rule;
  return tKey(t, rule.key, rule.params ?? {});
}

/** Phrase d'en-tête à partir des compteurs du rapport. */
export function translateImageLinkHeadline(
  t: TranslateFn,
  headline: ImageLinkReport["headline"] | string,
): string {
  // Anciens jobs / payload string.
  if (typeof headline === "string") return headline;

  const { auto, total, records, needsDecision, unlinkable } = headline;
  if (total === 0) return tKey(t, "bo.imageLink.headline.empty");

  const recordsPart =
    records > 0
      ? tKey(t, "bo.imageLink.headline.recordsPart", { records })
      : "";

  const parts = [
    tKey(t, "bo.imageLink.headline.main", {
      auto,
      total,
      recordsPart,
    }),
  ];
  if (needsDecision > 0) {
    parts.push(
      tKey(t, "bo.imageLink.headline.needsDecision", { count: needsDecision }),
    );
  }
  if (unlinkable > 0) {
    parts.push(
      tKey(t, "bo.imageLink.headline.unlinkable", { count: unlinkable }),
    );
  }
  return `${parts.join(". ")}.`;
}
